import React, { useEffect, useRef, useState } from "react";
import notifications from "../../../common/Components/Notifications/Notification";
import { isKarkhanaSupervisor } from "../../../helpers/karkhanaEnabled";
import { createKarkhanaWorkerProfile, getWorkerCodePreview, listWorkerCategories, uploadWorkerPhoto } from "../../../services/karkhanaApi";
import { useKT } from "../i18n/kt";
import { shrinkPhoto } from "./photo";
import "./quick-add.scss";

export type AddedPerson = { id: string; code: string; name: string; photoUrl?: string | null };

/** Work types offered before the studio has its own (they are set up on the first add). */
const STARTER = [
  { role: "WEAVER", label: "Weaver", prefix: "K" },
  { role: "HELPER", label: "Helper", prefix: "J" },
  { role: "DYER", label: "Dyer", prefix: "D" },
  { role: "TAANI", label: "Warping", prefix: "T" },
];

const readRole = (): string | undefined => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

type Choice = { key: string; label: string; categoryId?: string; role?: string; prefix?: string };

/**
 * Add a new person right where they are needed (starting a saree, attendance, job work): name, work,
 * optional mobile and photo. HKP gives the code and makes the worker; everything else can be filled
 * in later in Workers. Opens as a sheet from the bottom on phones, a dialog on bigger screens.
 */
const QuickAddPerson = ({
  initialName = "",
  role = "WEAVER",
  title,
  onDone,
  onCancel,
  extra,
  submitLabel,
  docked = false,
}: {
  initialName?: string;
  /** The work picked by default: WEAVER, HELPER… */
  role?: string;
  title?: string;
  /** Called with the new person once saved (and their photo, if one was taken). */
  onDone: (p: AddedPerson) => void | Promise<void>;
  onCancel: () => void;
  /** More fields of the place it opened from (e.g. "Mark in now"). */
  extra?: React.ReactNode;
  submitLabel?: string;
  /** Always open beside a list (tablet / desktop attendance), not a sheet over the page. */
  docked?: boolean;
}) => {
  const { t } = useKT();
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState("");
  const [choices, setChoices] = useState<Choice[]>([]);
  const [pick, setPick] = useState<string>("");
  // Most people are weavers or helpers: four work types first, the rest behind "More…".
  const [allWork, setAllWork] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nextCode, setNextCode] = useState<string | null>(null);
  // Someone with this name was added moments ago (often by another supervisor at the same time).
  const [sameAs, setSameAs] = useState<AddedPerson | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    listWorkerCategories()
      .then((res: any) => {
        if (!live) return;
        const cats: any[] = res?.success ? res.data || [] : [];
        const active = cats.filter((c) => c.active !== false);
        // Weavers and helpers first: most new people are one of them.
        const rank = (c: any) => {
          const i = ["WEAVER", "HELPER", "DYER", "TAANI"].indexOf(c.userRole);
          return i >= 0 ? i : c.canWeave ? 0 : c.canHelp ? 1 : 9;
        };
        active.sort((a, b) => rank(a) - rank(b));
        const list: Choice[] = active.length
          ? active.map((c) => ({ key: c.id, label: c.name || c.prefix, categoryId: c.id, prefix: c.prefix }))
          : STARTER.map((s) => ({ key: s.role, label: s.label, role: s.role, prefix: s.prefix }));
        setChoices(list);
        const wanted =
          list.find((c) => c.role === role) ||
          active.find((c) => c.userRole === role) ||
          (role === "WEAVER" ? active.find((c) => c.canWeave) : role === "HELPER" ? active.find((c) => c.canHelp) : null);
        setPick(wanted ? (wanted.key || wanted.id) : list[0]?.key || "");
      })
      .catch(() => {
        if (!live) return;
        const list = STARTER.map((s) => ({ key: s.role, label: s.label, role: s.role, prefix: s.prefix }));
        setChoices(list);
        setPick(list.find((c) => c.role === role)?.key || list[0].key);
      });
    // A docked form is on screen all day: don't pull the keyboard or focus to it on page load.
    if (!docked) nameRef.current?.focus();
    return () => {
      live = false;
    };
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show the code HKP will give for the chosen work, so it can be written on the person's card.
  const pickedPrefix = choices.find((c) => c.key === pick)?.prefix;
  useEffect(() => {
    setNextCode(null);
    if (!pickedPrefix) return;
    let live = true;
    getWorkerCodePreview(pickedPrefix)
      .then((res: any) => live && setNextCode(res?.success ? res.data?.code || null : null))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [pickedPrefix]);

  useEffect(() => {
    if (!photo) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const save = async (allowSameName = false) => {
    const clean = name.trim().replace(/\s+/g, " ");
    if (!clean) {
      notifications({ title: t("Error"), message: t("Enter the name"), type: "danger" });
      return;
    }
    const digits = phone.replace(/\D/g, "").replace(/^(91|0)(?=\d{10}$)/, "");
    if (phone.trim() && digits.length !== 10) {
      notifications({ title: t("Error"), message: t("A mobile number has 10 digits"), type: "danger" });
      return;
    }
    const choice = choices.find((c) => c.key === pick);
    setBusy(true);
    try {
      const res: any = await createKarkhanaWorkerProfile({
        name: clean,
        phone: digits || undefined,
        ...(choice?.categoryId ? { categoryIds: [choice.categoryId] } : { role: choice?.role || role }),
        ...(allowSameName ? { allowSameName: true } : {}),
      });
      const same = res?.data?.sameAs;
      if (!res?.success && same?.id) {
        setSameAs({ id: same.id, code: same.workerCode, name: same.name });
        return;
      }
      setSameAs(null);
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Something went wrong"), type: "danger" });
        return;
      }
      const person: AddedPerson = { id: res.data.id, code: res.data.workerCode, name: res.data.name };
      if (res.data.pendingConfirmation) {
        // A supervisor's add waits for the owner before any pay or job work; say so now, not at the first Pay.
        notifications({
          title: t("Added {{code}}", { code: res.data.workerCode }),
          message: t("The owner must confirm {{name}} before pay or job work.", { name: res.data.name }),
          type: "info",
          duration: 5000,
        });
      }
      if (photo) {
        try {
          const small = await shrinkPhoto(photo);
          person.photoUrl = await uploadWorkerPhoto(person.id, small.blob, small.type || "image/jpeg");
        } catch {
          // The person is added either way; the photo can be taken again from Workers.
          notifications({ title: t("Photo not saved"), message: t("{{name}} is added. Take the photo again later.", { name: clean }), type: "warning" });
        }
      }
      await onDone(person);
    } catch {
      notifications({ title: t("No signal"), message: t("This needs a connection. Try again in a moment."), type: "danger" });
    } finally {
      setBusy(false);
    }
  };

  const supervisor = isKarkhanaSupervisor(readRole());
  return (
    <>
      {docked ? null : <div className="kq-backdrop" onClick={busy ? undefined : onCancel} />}
      <section
        className={`kq${docked ? " is-docked" : ""}`}
        role={docked ? "region" : "dialog"}
        aria-modal={docked ? undefined : true}
        aria-label={title || t("New person")}
      >
        <div className="kq__grab" aria-hidden="true" />
        <h3>{title || t("New person")}</h3>
        {nextCode ? (
          <p className="kq__code">
            {t("Code")} <strong>{nextCode}</strong> <span>{t("given when you save")}</span>
          </p>
        ) : null}
        <div className="kq__photo">
          <button type="button" className={`kq__shot${preview ? " has-photo" : ""}`} onClick={() => fileRef.current?.click()} aria-label={t("Take photo")}>
            {preview ? <img src={preview} alt="" /> : <span aria-hidden="true">📷</span>}
          </button>
          <div>
            <strong>{preview ? t("Photo taken ✓") : t("Add photo")}</strong>
            <span>{preview ? t("Tap to take it again") : t("Optional · camera opens")}</span>
            {preview ? (
              <button type="button" className="kq__remove" onClick={() => setPhoto(null)}>
                {t("Remove photo")}
              </button>
            ) : null}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              setPhoto(e.target.files?.[0] || null);
              e.target.value = "";
            }}
          />
        </div>
        <label className="kq__label" htmlFor="kq-name">
          {t("Name")}
        </label>
        <input id="kq-name" ref={nameRef} className="kq__input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        <span className="kq__label">{t("Works as")}</span>
        <div className="kq__chips" role="radiogroup" aria-label={t("Works as")}>
          {(allWork || choices.length <= 5 ? choices : choices.filter((c, i) => i < 4 || c.key === pick)).map((c) => (
            <button key={c.key} type="button" role="radio" aria-checked={pick === c.key} className={`kr-choice${pick === c.key ? " is-on" : ""}`} onClick={() => setPick(c.key)}>
              {t(c.label)}
            </button>
          ))}
          {!allWork && choices.length > 5 ? (
            <button type="button" className="kr-choice" onClick={() => setAllWork(true)}>
              {t("More…")}
            </button>
          ) : null}
        </div>
        <label className="kq__label" htmlFor="kq-phone">
          {t("Mobile")} <i>{t("optional")}</i>
        </label>
        <input id="kq-phone" className="kq__input" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder={t("10 digits")} autoComplete="off" />
        {extra}
        <p className="kq__note">
          {supervisor
            ? t("HKP gives the code. The owner confirms them before pay or job work; the office adds bank and KYC later.")
            : t("HKP gives the code. Bank, KYC and pay can be added later in Workers.")}
        </p>
        {sameAs ? (
          <div className="kq__same" role="alert">
            <p>{t("{{name}} was just added as {{code}}. Same person?", { name: sameAs.name, code: sameAs.code })}</p>
            <div className="kq__foot">
              <button type="button" className="btn ResetBtn" disabled={busy} onClick={() => onDone(sameAs)}>
                {t("Use {{code}}", { code: sameAs.code })}
              </button>
              <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => save(true)}>
                {t("Add another")}
              </button>
            </div>
          </div>
        ) : null}
        <div className="kq__foot" hidden={!!sameAs}>
          {docked ? null : (
            <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={onCancel}>
              {t("Cancel")}
            </button>
          )}
          <button type="button" className="btn ResetBtn" disabled={busy || !name.trim()} onClick={() => save()}>
            {busy ? t("Saving…") : submitLabel || t("Add & use")}
          </button>
        </div>
      </section>
    </>
  );
};

export default QuickAddPerson;
